/* eslint-disable max-len */
import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  Icon,
  Dropdown,
  FocusTrapZone,
  Overlay,
  SearchBox, Stack,
  ResponsiveMode,
} from '@bingads-webui-react/fabric-control';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { setClarityTag } from '@bingads-webui-universal/clarity';

import { CSMLTimeFrame,
  CSMLSortBy,
  GridSectionName,
  CSMLSearchLocation,
  ROOT_FOLDER_ID,
  DEFAULT_PAGE_SIZE,
  AssetLibraryPageName,
  getAssetSourceOptions } from '@bingads-webui-campaign-react/asset-library-model';

import {
  DateAddedFilter,
  ImageSizeFilter,
  ImportSourceFilter,
  AssetTypeFilter,
  DEFAULT_GRID_CONFIG,
  VideoDurationFilter,
  SearchLocationFilter,
  useSearchBoxFilter,
  SectionTilesGrid,
  FolderPathBreadcrumb,
  ToggleButtonFilter,
  AssetSourceFilter,
} from '@bingads-webui-campaign-react/asset-library-components';

import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';

import { useImportSource } from './hooks/use-import-source';
import { useImageSizeFilter } from './hooks/use-image-size-filter';
import { useDateAddedFilter } from './hooks/use-date-added-filter';
import { useSortBy } from './hooks/use-sort-by';
import { useAssetTypeFilter } from './hooks/use-asset-type-filter';
import { useVideoDurationFilter } from './hooks/use-video-duration-filter';
import { useSearchLocationFilter } from './hooks/use-search-location-filter';
import { useAssetSourcesFilter } from './hooks/use-asset-sources-filter';

import {
  AssetLibraryFoldersGridLoadingCell,
  AssetLibraryAssetsGridLoadingCell,
} from './cells/index';
import { getAssetLibraryLandingPageContentStyle } from './styles';
import { getUpdatedAssets } from './merge-task-updates';
import { EmptyView } from './empty-view';
import {
  isSectionDataLoadingStatus,
  isSectionDataEmptyStatus,
} from './sections-utils';

const overlaySpinnerStyles = { margin: 100 };
const bottomSpinnerStyles = { marginTop: 20, marginBottom: 100 };

const pageHeaderTokens = { childrenGap: '4 24' };

const FOLDER_GRID_CONFIG = { ...DEFAULT_GRID_CONFIG, aspectRatio: undefined, rowHeight: 110 };

const AssetLibraryLandingPageContentInternal = ({
  dataService,
  renderBanner,
  renderHeader,
  renderGridCell,
  onFailedToLoadData,
  dateFormat,
  ianaTimeZone,
  assetLibraryUserPreferences,
  assetLibraryAccountPreferences,
  reload,
  logger,
  i18n,
  dropdownWidth,
  classes,
  tokens,
  dataLoaded,
  displayFolderPath,
  reloadingFolders,
  assetsToLoadForSubfolders,
  renderToolbar,

  // multi-selection props
  multiSelectionEnabled,
  onSelectionChange,
  onFolderNavigationChange,
  sectionSelectedItems,

  allowedAssetType,

  breadcrumbVisibleForRootFolder,

  //
  searchLocationFilterState,
  setSearchLocationFilterState,

  realUsedFlagSuffix,
  isAssetLibraryAIGCMVPEnabled,
  isClickableOutsideFocusTrap,

  disabledAssetSources,
}) => {
  // states
  const {
    searchText,
    onSearchTextChanging,
    onSearchTextBlur,
    onSearchTextChange,
    onSearchTextClear,
  } = useSearchBoxFilter({
    logger,
    preference: assetLibraryAccountPreferences,
    realUsedFlagSuffix,
  });
  const {
    minWidthFilter,
    minHeightFilter,
    onImageSizeFilterChange,
  } = useImageSizeFilter({
    logger,
    preference: assetLibraryAccountPreferences,
    realUsedFlagSuffix,
  });
  const {
    dateAddedChoiceOptions,
    dateAddedFilter,
    dateAddedFilterCustomDateRange,
    onDateAddedFilterChange,
  } = useDateAddedFilter({
    logger,
    i18n,
    ianaTimeZone,
    preference: assetLibraryAccountPreferences,
    realUsedFlagSuffix,
  });
  const {
    importSource,
    importSourceOptions,
    onImportSourceFilterChange,
  } = useImportSource({
    logger,
    i18n,
    preference: assetLibraryAccountPreferences,
    realUsedFlagSuffix,
  });

  const {
    assetSources, assetSourceOptions, onAssetSourceFilterChange,
  } = useAssetSourcesFilter({
    logger, i18n, preference: assetLibraryAccountPreferences, realUsedFlagSuffix, disabledAssetSources,
  });

  const {
    sortBy,
    sortingOptions,
    setSortBy,
    onSortByChange,
  } = useSortBy({
    logger,
    preference: assetLibraryUserPreferences,
    i18n,
    accountPreference: assetLibraryAccountPreferences,
    realUsedFlagSuffix,
  });

  const {
    assetTypeFilterOptions,
    onAssetTypeChange,
    assetTypeFilter,
  } = useAssetTypeFilter({
    logger,
    i18n,
    preference: assetLibraryAccountPreferences,
    realUsedFlagSuffix,
  });

  const {
    minDurationFilter,
    maxDurationFilter,
    onVideoDurationFilterChange,
  } = useVideoDurationFilter({
    logger,
    preference: assetLibraryAccountPreferences,
    realUsedFlagSuffix,
  });

  const {
    searchLocationFilter,
    searchLocationOptions,
    onSearchLocationChange,
  } = useSearchLocationFilter({
    searchLocationFilter: searchLocationFilterState,
    setSearchLocationFilter: setSearchLocationFilterState,
    isInRootFolder: dataService && dataService.currentFolderId === ROOT_FOLDER_ID,
    logger,
    i18n,
    preference: assetLibraryAccountPreferences,
    realUsedFlagSuffix,
  });

  const [isTopRatedFilterChecked, setIsTopRatedFilterChecked] = useState(false);
  const isAnyFilterSet = searchText || importSource || assetTypeFilter || searchLocationFilter
      || minWidthFilter || minHeightFilter || minDurationFilter || maxDurationFilter
      || (dateAddedFilter && dateAddedFilter !== CSMLTimeFrame.Anytime)
      || isTopRatedFilterChecked || !_.isEmpty(assetSources);

  const {
    assets,
    totalAssetCount,
    totalSubfoldersCount,
    imageCroppingTasks,
    loadData,
    loadMoreData,
    folderPath,
    navigateToFolderInPath,
    folders,
    currentFolderId,
    videoUpdated,
    dataOwnerPageName,
  } = dataService || {};

  // load assets for initial page load, or after any filters are changed
  const loadedAssetList = useMemo(() => getUpdatedAssets({
    assets,
    updates: {
      imageCroppingTasks,
      videoUpdated,
    },
  }), [assets, imageCroppingTasks, videoUpdated]);

  const searchBoxPlaceholder = i18n.getString(_TL_('Search by asset and folder name or ID'));

  const preDefinedAssetSources = useMemo(() => {
    if (_.isEmpty(disabledAssetSources)) {
      return [];
    }
    const assetOptions = getAssetSourceOptions(i18n);
    const assetOptionsWithoutDisabledSources = assetOptions.filter(option => !_.contains(disabledAssetSources, option.key));
    const assetOptionsKeys = assetOptionsWithoutDisabledSources.map(option => option.key);
    return assetOptionsKeys;
  }, [i18n, disabledAssetSources]);

  const renderFilters = useCallback(() => (
    <React.Fragment>
      <div className="asset-library-search">
        <SearchBox
          placeholder={searchBoxPlaceholder}
          showIcon
          defaultValue={searchText || ''}
          onSearch={onSearchTextChange}
          onClear={onSearchTextClear}
          onChange={onSearchTextChanging}
          onBlur={onSearchTextBlur}
        />
      </div>
      <Stack horizontal wrap tokens={tokens}>
        { !allowedAssetType &&
          <div className="asset-type-filter">
            <AssetTypeFilter
              defaultAssetType={assetTypeFilter}
              assetTypeFilterOptions={assetTypeFilterOptions}
              placeholder={i18n.getString(_TL_('Asset type'))}
              onChange={onAssetTypeChange}
              i18n={i18n}
              dropdownWidth={dropdownWidth}
            />
          </div>
        }
        <div className="asset-library-image-size-filter">
          <ImageSizeFilter
            defaultMinHeight={minHeightFilter}
            defaultMinWidth={minWidthFilter}
            onChange={onImageSizeFilterChange}
            i18n={i18n}
            dropdownWidth={dropdownWidth}
          />
        </div>
        <div className="asset-library-date-added-filter">
          <DateAddedFilter
            defaultTimeFrame={dateAddedFilter}
            defaultCustomDateRange={dateAddedFilterCustomDateRange}
            choiceOptions={dateAddedChoiceOptions}
            onChange={onDateAddedFilterChange}
            timeZone={ianaTimeZone}
            dateFormat={dateFormat}
            dropdownWidth={dropdownWidth}
            i18n={i18n}
          />
        </div>
        {
          isAssetLibraryAIGCMVPEnabled && (
          <div className="asset-library-asset-source-filter">
            <AssetSourceFilter
              defaultSelectedAssetSources={assetSources}
              assetSourceOptions={assetSourceOptions}
              onChange={onAssetSourceFilterChange}
              dropdownWidth="auto"
              i18n={i18n}
            />
          </div>
        )}
        {
          !isAssetLibraryAIGCMVPEnabled && (
          <div className="asset-library-import-source-filter">
            <ImportSourceFilter
              defaultImportSource={importSource}
              choiceOptions={importSourceOptions}
              onChange={onImportSourceFilterChange}
              dropdownWidth={dropdownWidth}
              i18n={i18n}
            />
          </div>
        )}
        {
          !allowedAssetType &&
          <div className="video-duration-filter">
            <VideoDurationFilter
              defaultMinDuration={minDurationFilter}
              defaultMaxDuration={maxDurationFilter}
              onChange={onVideoDurationFilterChange}
              dropdownWidth={dropdownWidth}
              i18n={i18n}
            />
          </div>
        }
        {
          currentFolderId === ROOT_FOLDER_ID &&
          <div className="search-location-filter">
            <SearchLocationFilter
              defaultLocation={searchLocationFilter}
              locationFilterOptions={searchLocationOptions}
              dropdownWidth={dropdownWidth}
              onChange={onSearchLocationChange}
              i18n={i18n}
            />
          </div>
        }
        <div className="top-rated-performance-filter">
          <ToggleButtonFilter
            text={i18n.getString(_TL_('Top-rated'))}
            iconName="Calories"
            checked={isTopRatedFilterChecked}
            onChecked={setIsTopRatedFilterChecked}
          />
        </div>
      </Stack>
    </React.Fragment>
  ), [searchBoxPlaceholder, searchText, onSearchTextChange, onSearchTextClear, onSearchTextChanging, onSearchTextBlur, tokens, allowedAssetType, assetTypeFilter, assetTypeFilterOptions, i18n, onAssetTypeChange, dropdownWidth, minHeightFilter, minWidthFilter, onImageSizeFilterChange, dateAddedFilter, dateAddedFilterCustomDateRange, dateAddedChoiceOptions, onDateAddedFilterChange, ianaTimeZone, dateFormat, isAssetLibraryAIGCMVPEnabled, assetSources, assetSourceOptions, onAssetSourceFilterChange, importSource, importSourceOptions, onImportSourceFilterChange, minDurationFilter, maxDurationFilter, onVideoDurationFilterChange, currentFolderId, searchLocationFilter, searchLocationOptions, onSearchLocationChange, isTopRatedFilterChecked]);

  const onRenderSortOption = useCallback(({ text, icon }) => (
    <div className="asset-library-sort-option">
      <span>{text}</span>
      {icon && (<Icon iconName={icon} aria-hidden title={icon} style={{ marginLeft: 2 }} />)}
    </div>
  ), []);

  const renderSortBy = useCallback(() => (
    <div className="asset-library-sort">
      <Dropdown
        options={sortingOptions}
        selectedKey={sortBy || CSMLSortBy.MostRecent}
        onChange={onSortByChange}
        responsiveMode={ResponsiveMode.large}
        ariaLabel={i18n.getString(_TL_('sort option'))}
        dropdownWidth={dropdownWidth}
        onRenderOption={onRenderSortOption}
        onRenderTitle={options => onRenderSortOption(options[0])}
      />
    </div>
  ), [dropdownWidth, i18n, onRenderSortOption, onSortByChange, sortBy, sortingOptions]);

  const onRenderCell = useCallback(({ item, sectionName, ...others }) => {
    if (renderGridCell) {
      const cell = renderGridCell({
        item, searchText, sectionName, ...others,
      });
      if (cell) return cell;
    }
    return null;
  }, [renderGridCell, searchText]);

  const [isPageInited, setPageInited] = useState(false);
  const [isGridInited, setGridInited] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingMoreData, setLoadingMoreData] = useState(false);

  const onRenderLoadingCell = useCallback(({ onLoading, sectionName }) => {
    // ensure not loading next page before initial data loading is done
    const onLoadingData = isPageInited && isGridInited && !loadingMoreData ? onLoading : undefined;
    if (sectionName === GridSectionName.Folder) {
      return <AssetLibraryFoldersGridLoadingCell onLoading={onLoadingData} />;
    }
    return <AssetLibraryAssetsGridLoadingCell onLoading={onLoadingData} />;
  }, [isGridInited, isPageInited, loadingMoreData]);

  const loadDataAsync = useCallback(() => {
    // get selected date range for date added filter
    let dateAddedDateRange;
    if (dateAddedFilter === CSMLTimeFrame.Custom) {
      dateAddedDateRange = dateAddedFilterCustomDateRange;
    } else {
      const { range } = _.find(dateAddedChoiceOptions, o => o.key === dateAddedFilter) || {};
      dateAddedDateRange = range;
    }

    const isGlobalSearch = searchLocationFilter === CSMLSearchLocation.Global && currentFolderId === ROOT_FOLDER_ID;

    const usePreDefinedAssetSources = preDefinedAssetSources.length > 0 && assetSources.length === 0;

    return loadData({
      searchText,
      minImageWidth: minWidthFilter,
      minImageHeight: minHeightFilter,
      minVideoDuration: minDurationFilter,
      maxVideoDuration: maxDurationFilter,
      dateAddedDateRange,
      sortBy,
      importSource,
      assetSources: usePreDefinedAssetSources ? preDefinedAssetSources : assetSources,
      assetsToLoadForSubfolders,
      assetTypeFilter,
      isGlobalSearch,
      includeParentHierarchy: isGlobalSearch, // include parent hierarchy when global searching
      topRated: isTopRatedFilterChecked,
      pageName: AssetLibraryPageName.LandingPage,
    });
  }, [
    dateAddedFilter,
    loadData,
    searchText,
    minWidthFilter,
    minHeightFilter,
    sortBy,
    importSource,
    dateAddedFilterCustomDateRange,
    dateAddedChoiceOptions,
    assetsToLoadForSubfolders,
    assetTypeFilter,
    minDurationFilter,
    maxDurationFilter,
    searchLocationFilter,
    currentFolderId,
    isTopRatedFilterChecked,
    assetSources,
    preDefinedAssetSources,
  ]);

  const onRenderHeader = useCallback(() => (
    !renderHeader ? (
      <Stack horizontal wrap horizontalAlign="space-between" verticalAlign="center" tokens={tokens}>
        <Stack horizontal wrap tokens={tokens}>
          {renderFilters()}
        </Stack>
        <Stack horizontal wrap tokens={tokens}>
          {renderSortBy()}
        </Stack>
      </Stack>
    ) : (
      renderHeader({ renderFilters, renderSortBy })
    )
  ), [renderFilters, renderHeader, renderSortBy, tokens]);

  const foldersGridSection = useMemo(() => ({
    sectionName: GridSectionName.Folder,
    sectionTitle: i18n.getString(_TL_('Folders')),
    sectionClassName: 'asset-library-folders-section',
    itemKey: 'folderId',
    items: folders,
    totalItemCount: totalSubfoldersCount,
    config: FOLDER_GRID_CONFIG,
    selectedItems: sectionSelectedItems && sectionSelectedItems[GridSectionName.Folder],
  }), [folders, i18n, sectionSelectedItems, totalSubfoldersCount]);

  const assetsGridSection = useMemo(() => ({
    sectionName: GridSectionName.Asset,
    sectionTitle: i18n.getString(_TL_('Assets')),
    sectionClassName: 'asset-library-assets-section',
    itemKey: 'id',
    items: loadedAssetList,
    totalItemCount: totalAssetCount,
    selectedItems: sectionSelectedItems && sectionSelectedItems[GridSectionName.Asset],
  }), [i18n, loadedAssetList, sectionSelectedItems, totalAssetCount]);

  const sectionGridData = useMemo(
    () => ([foldersGridSection, assetsGridSection]),
    [assetsGridSection, foldersGridSection]
  );

  // init page
  useEffect(() => {
    if (!isPageInited && assetLibraryUserPreferences) {
      setSortBy(assetLibraryUserPreferences.sortBy || CSMLSortBy.MostRecent);
      setPageInited(true);
    }
  }, [assetLibraryUserPreferences, isPageInited, setSortBy]);

  useEffect(() => {
    if (isAssetLibraryAIGCMVPEnabled) {
      setClarityTag({ AssetLibraryCopilot: 'true' });
    }
  }, [isAssetLibraryAIGCMVPEnabled]);

  // load data for initial page load
  useEffect(() => {
    if (!isPageInited) {
      // wait until page is initialized
      return;
    }

    if (reload) { // Important! Add a trivial log so that reload will be an explicit dep.
      logger.logTrace('force to reload');
    }

    setLoadingData(true);
    loadDataAsync()
      .tapCatch((err) => { // throw out so that this error will be shown in console for easy debugging
        onFailedToLoadData(err);
      }).finally(() => {
        setLoadingData(false);
        dataLoaded();
        setGridInited(true);
      });
  }, [isPageInited, loadDataAsync, onFailedToLoadData, logger, dataLoaded, reload]);

  const loadNextPage = useCallback(() => {
    setLoadingMoreData(true);
    loadMoreData({ count: DEFAULT_PAGE_SIZE }).catch((err) => {
      onFailedToLoadData(err);
    }).finally(() => setLoadingMoreData(false));
  }, [loadMoreData, onFailedToLoadData]);

  const isPageLoading = !isPageInited
    || loadingData || isSectionDataLoadingStatus(sectionGridData) || reloadingFolders;

  const onFolderPathClicked = useCallback((item) => {
    logger.logTrace('Navigate to folder in path', { folderId: item.folderId });
    onFolderNavigationChange();
    navigateToFolderInPath({ id: item.folderId });
  }, [navigateToFolderInPath, onFolderNavigationChange, logger]);

  const hadNoData = isSectionDataEmptyStatus(sectionGridData);

  const bulkTookbar = useMemo(() => (renderToolbar && renderToolbar()), [renderToolbar]);

  return (
    <div className={classnames('asset-library-page', classes.container, classes.injectedContentContainer)}>
      <FocusTrapZone isClickableOutsideFocusTrap={isClickableOutsideFocusTrap}>
        {/* banner */}
        {renderBanner && renderBanner()}
        {/*  grid header or toolbar */}
        {!bulkTookbar ? (
          <div className="asset-library-page-header">
            {onRenderHeader()}
          </div>
        ) : bulkTookbar}
        {/* display folder path breadcrumb under filter */}
        {displayFolderPath && <FolderPathBreadcrumb
          folderPath={folderPath}
          onItemClick={onFolderPathClicked}
          visibleForRootFolder={breadcrumbVisibleForRootFolder}
        />}

        {/* grid content */}
        <div className="asset-library-grid-container">
          {(!dataOwnerPageName || dataOwnerPageName === AssetLibraryPageName.LandingPage) && (
            <SectionTilesGrid
              sectionData={sectionGridData}
              onRenderCell={onRenderCell}
              onRenderLoadingCell={onRenderLoadingCell}
              loadNextPage={loadNextPage}
              multiSelectionEnabled={multiSelectionEnabled}
              onSelectionChange={onSelectionChange}
              isLoadingData={isPageLoading || loadingMoreData}
            />
          )}
          {/* show loading indicator */}
          {isPageLoading && (
            <Overlay >
              <AssetLibrarySpinner className="page-load-spinner" useCustomSize customStyle={overlaySpinnerStyles} />
            </Overlay>
          )}
          {/* show spinner in the bottom */}
          {!isPageLoading && loadingMoreData && (
            <AssetLibrarySpinner className="page-load-spinner" useCustomSize customStyle={bottomSpinnerStyles} />
          )}
        </div>

        {/* no content */}
        {(!isPageLoading && hadNoData) && (
          <div className={classnames('asset-library-no-data', classes.emptyViewContainer)}>
            <EmptyView i18n={i18n} isInSubfolder={!!(folderPath && folderPath.length > 1)} isFilterSet={isAnyFilterSet} />
          </div>
        )}
      </FocusTrapZone>
    </div>
  );
};

AssetLibraryLandingPageContentInternal.propTypes = {
  dataService: PropTypes.shape({
    assets: PropTypes.arrayOf(PropTypes.shape({})),
    totalAssetCount: PropTypes.number,
    imageCroppingTasks: PropTypes.shape({}),
    loadData: PropTypes.func,
    loadMoreData: PropTypes.func,
  }).isRequired,
  onFailedToLoadData: PropTypes.func,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }).isRequired,
  ianaTimeZone: PropTypes.string.isRequired,
  dateFormat: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    getToday: PropTypes.func,
    formatCivilDate: PropTypes.func,
  }).isRequired,
  assetLibraryUserPreferences: PropTypes.shape({
    sortBy: PropTypes.string,
    setSortBy: PropTypes.func,
  }),
  assetLibraryAccountPreferences: PropTypes.shape({}),
  // injected styles
  classes: PropTypes.shape({
    injectedContentContainer: PropTypes.string,
    container: PropTypes.string,
    emptyViewContainer: PropTypes.string,
  }).isRequired,
  reload: PropTypes.shape({}), // if changed, force to reload
  renderBanner: PropTypes.func,
  renderHeader: PropTypes.func,
  renderGridCell: PropTypes.func,
  renderToolbar: PropTypes.func,
  dropdownWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  tokens: PropTypes.shape({}),
  dataLoaded: PropTypes.func,
  displayFolderPath: PropTypes.bool,
  reloadingFolders: PropTypes.bool,
  assetsToLoadForSubfolders: PropTypes.number,

  // multi selection
  multiSelectionEnabled: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  onFolderNavigationChange: PropTypes.func,
  sectionSelectedItems: PropTypes.shape({}),

  allowedAssetType: PropTypes.string,

  breadcrumbVisibleForRootFolder: PropTypes.bool,

  searchLocationFilterState: PropTypes.string,
  setSearchLocationFilterState: PropTypes.func,

  realUsedFlagSuffix: PropTypes.string,
  isAssetLibraryAIGCMVPEnabled: PropTypes.bool,
  isClickableOutsideFocusTrap: PropTypes.bool,

  disabledAssetSources: PropTypes.arrayOf(PropTypes.string),
};

AssetLibraryLandingPageContentInternal.defaultProps = {
  assetLibraryUserPreferences: null,
  assetLibraryAccountPreferences: null,
  reload: null,
  renderBanner: null,
  renderHeader: null,
  renderGridCell: null,
  renderToolbar: null,
  onFailedToLoadData: () => null,
  dropdownWidth: 0,
  tokens: pageHeaderTokens,
  dataLoaded: () => null,
  displayFolderPath: false,
  reloadingFolders: false,

  multiSelectionEnabled: false,
  onSelectionChange: undefined,
  onFolderNavigationChange: _.noop,
  sectionSelectedItems: undefined,

  assetsToLoadForSubfolders: 1,
  allowedAssetType: null,

  breadcrumbVisibleForRootFolder: false,

  searchLocationFilterState: undefined,
  setSearchLocationFilterState: undefined,

  realUsedFlagSuffix: '',
  isAssetLibraryAIGCMVPEnabled: false,
  isClickableOutsideFocusTrap: false,

  disabledAssetSources: [],
};

export const AssetLibraryLandingPageContent
  = withDefaultStyles(AssetLibraryLandingPageContentInternal, getAssetLibraryLandingPageContentStyle);
